import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { setAuthContext } from './global/globalAuthRef';
import { ROLE_TYPE } from '@/api/type/commonTypes';
import { LOCAL_STORAGE_KEYS } from '@/constants/storage';

export interface AuthContextType {
  isLoggedIn: boolean;
  roleType: ROLE_TYPE;
  login: (token: string, role: ROLE_TYPE) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType>({
  isLoggedIn: false,
  roleType: 'CENTER',
  login: () => {},
  logout: () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(
    !!localStorage.getItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN)
  );
  const [roleType, setIsRoleType] = useState<ROLE_TYPE>(
    (localStorage.getItem(LOCAL_STORAGE_KEYS.ROLE_TYPE) as ROLE_TYPE) ||
      'PLAYER'
  );
  const navigate = useNavigate();
  const location = useLocation();

  const login = useCallback(
    (token: string, role: ROLE_TYPE) => {
      localStorage.setItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN, token);
      localStorage.setItem(LOCAL_STORAGE_KEYS.ROLE_TYPE, role);
      setIsLoggedIn(true);
      setIsRoleType(role);
      navigate('/player-list');
    },
    [navigate]
  );

  const logout = useCallback(() => {
    localStorage.removeItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.ROLE_TYPE);
    setIsLoggedIn(false);
    navigate('/login');
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
    setIsLoggedIn(!!token);
  }, []);

  useEffect(() => {
    setAuthContext({ isLoggedIn, roleType, login, logout });
  }, [isLoggedIn, login, logout, roleType]);

  return (
    <AuthContext.Provider value={{ isLoggedIn, roleType, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
